import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Divider,
  Alert,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText
} from '@mui/material';
import {
  AttachMoney,
  Delete,
  Add,
  CheckCircle,
  AutoFixHigh
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { createPlan, updatePlan, deletePlan, getPlans, getServiceTypes } from '../../services/planService';
import { translateTextStream } from '@services/aiTranslateService';

interface ServiceTypeOption {
  _id: string;
  name: string;
  zh_name: string;
  subServiceTypes?: SubServiceTypeOption[];
  [key: string]: unknown;
}

interface SubServiceTypeOption {
  _id: string;
  name: string;
  zh_name: string;
  [key: string]: unknown;
}

interface SuggestedService {
  id: string;
  name: string;
  zh_name: string;
  description: string;
  zh_description: string;
  price: string | number;
  serviceTypeId?: string;
  subServiceTypeId?: string | null;
  isEnabled: boolean;
  source: 'ai' | 'custom';
  isActive?: boolean;
  [key: string]: unknown;
}

interface TranslatingState {
  enToZh?: boolean;
  zhToEn?: boolean;
}

interface InstagramShowcaseFlowData {
  staffId?: string | null;
  businessId?: string | null;
  [key: string]: unknown;
}

interface ServiceCreationStepProps {
  flowData: InstagramShowcaseFlowData;
  onUpdate: (data: Record<string, unknown>) => void;
  onComplete: () => void;
  onBack: () => void;
  onError: (message: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const ServiceCreationStep = ({
  flowData,
  onUpdate,
  onComplete,
  onBack,
  onError,
  isLoading,
  setIsLoading
}: ServiceCreationStepProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [suggestedServices, setSuggestedServices] = useState<SuggestedService[]>([]);
  const [customServices, setCustomServices] = useState<SuggestedService[]>([]);
  const [newServiceName, setNewServiceName] = useState<string>('');
  const [newServiceZhName, setNewServiceZhName] = useState<string>('');
  const [newServicePrice, setNewServicePrice] = useState<string>('');
  const [newServiceTypeId, setNewServiceTypeId] = useState<string>('');
  const [newSubServiceTypeId, setNewSubServiceTypeId] = useState<string>('');

  // Service types for custom service creation
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeOption[]>([]);
  const [subServiceTypes, setSubServiceTypes] = useState<SubServiceTypeOption[]>([]);

  // Translation loading states for AI suggested services
  const [translatingServices, setTranslatingServices] = useState<Record<string, TranslatingState>>({});
  // Format: { [serviceId]: { enToZh: boolean, zhToEn: boolean } }

  // Load service types for custom service creation
  useEffect(() => {
    const loadServiceTypes = async (): Promise<void> => {
      try {
        const response = await getServiceTypes();
        const types = (Array.isArray(response) ? response : []) as ServiceTypeOption[];
        setServiceTypes(types);
        console.log('Loaded service types:', types.length);
      } catch (error: unknown) {
        console.error('Error loading service types:', error);
      }
    };

    loadServiceTypes();
  }, []);

  // Load existing AI-generated plans (created automatically during AI analysis)
  useEffect(() => {
    const loadAISuggestedPlans = async (): Promise<void> => {
      if (!flowData.staffId) return;

      try {
        console.log('Loading AI-generated plans for staff:', flowData.staffId);
        console.log('BusinessId:', flowData.businessId);

        // Fetch both inactive and active AI-generated plans
        const [inactiveResponse, activeResponse] = await Promise.all([
          getPlans({
            aiGenerated: true,
            isActive: false,
            staffId: flowData.staffId ?? undefined
          }),
          getPlans({
            aiGenerated: true,
            isActive: true,
            staffId: flowData.staffId ?? undefined
          })
        ]);

        const inactivePlans = inactiveResponse.data || [];
        const activePlans = activeResponse.data || [];

        console.log(`Found ${inactivePlans.length} AI-generated inactive plans`, inactivePlans.map((p: Record<string, unknown>) => ({
          id: p._id,
          name: p.name,
          businessId: p.businessId,
          createdAt: p.createdAt
        })));

        console.log(`Found ${activePlans.length} AI-generated active plans`, activePlans.map((p: Record<string, unknown>) => ({
          id: p._id,
          name: p.name,
          businessId: p.businessId,
          createdAt: p.createdAt
        })));

        // Combine both - active plans first (already created), then inactive (pending)
        const allPlans = [...activePlans, ...inactivePlans];

        // Map to component format
        const services: SuggestedService[] = allPlans.map((plan: Record<string, unknown>) => ({
          id: plan._id as string,
          name: plan.name as string,
          zh_name: plan.zh_name as string,
          description: plan.description as string,
          zh_description: (plan.zh_description as string) || '',
          price: (plan.price as string | number) || '', // Display empty instead of 0
          serviceTypeId: plan.serviceTypeId as string | undefined,
          subServiceTypeId: plan.subServiceTypeId as string | undefined,
          isEnabled: true,
          source: 'ai' as const,
          isActive: plan.isActive as boolean // Track if already active
        }));

        setSuggestedServices(services);
      } catch (error: unknown) {
        console.error('Error loading AI-generated plans:', error);
        // Non-critical error - user can still add custom services
      }
    };

    loadAISuggestedPlans();
  }, [flowData.staffId, flowData.businessId]);

  // Update sub-service types when service type changes
  useEffect(() => {
    if (newServiceTypeId) {
      const selectedType = serviceTypes.find(st => st._id === newServiceTypeId);
      if (selectedType && selectedType.subServiceTypes) {
        setSubServiceTypes(selectedType.subServiceTypes);
        setNewSubServiceTypeId(''); // Reset sub-service selection
      } else {
        setSubServiceTypes([]);
      }
    } else {
      setSubServiceTypes([]);
      setNewSubServiceTypeId('');
    }
  }, [newServiceTypeId, serviceTypes]);

  const handleServiceDescriptionTranslate = async (serviceId: string, sourceText: string, sourceLang: string, targetLang: string, targetField: string): Promise<void> => {
    if (!sourceText) {
      toast.info(t('common.sourceFieldEmpty', 'Source field is empty'));
      return;
    }

    // Set loading state for this specific service and direction
    const loadingKey = targetField === 'zh_description' ? 'enToZh' : 'zhToEn';
    setTranslatingServices(prev => ({
      ...prev,
      [serviceId]: { ...prev[serviceId], [loadingKey]: true }
    }));

    const setTargetState = (valueOrUpdater: string | ((prev: string) => string)): void => {
      setSuggestedServices(prevServices =>
        prevServices.map(service => {
          if (service.id === serviceId) {
            const currentValue = (service as Record<string, unknown>)[targetField] as string || '';
            const newValue = typeof valueOrUpdater === 'function'
              ? valueOrUpdater(currentValue)
              : valueOrUpdater;
            return { ...service, [targetField]: newValue };
          }
          return service;
        })
      );
    };

    try {
      await translateTextStream(
        sourceText,
        targetLang as 'en' | 'zh' | 'zh-HK' | 'zh-TW',
        sourceLang as 'en' | 'zh' | 'zh-HK' | 'zh-TW',
        setTargetState,
        () => {
          setTranslatingServices(prev => ({
            ...prev,
            [serviceId]: { ...prev[serviceId], [loadingKey]: false }
          }));
          toast.success(t('common.translationSuccess', 'Translation successful!'));
        },
        (error: Error) => {
          setTranslatingServices(prev => ({
            ...prev,
            [serviceId]: { ...prev[serviceId], [loadingKey]: false }
          }));
          toast.error(error.message || t('common.translationError', 'Translation failed'));
        }
      );
    } catch (error: unknown) {
      const err = error as Error;
      setTranslatingServices(prev => ({
        ...prev,
        [serviceId]: { ...prev[serviceId], [loadingKey]: false }
      }));
      toast.error(err.message || t('common.translationError', 'Translation failed'));
    }
  };

  const handleServicePriceChange = (serviceId: string, price: string): void => {
    setSuggestedServices(prev =>
      prev.map(service =>
        service.id === serviceId ? { ...service, price } : service
      )
    );
  };

  const handleServiceDescriptionChange = (serviceId: string, description: string): void => {
    setSuggestedServices(prev =>
      prev.map(service =>
        service.id === serviceId ? { ...service, description } : service
      )
    );
  };

  const handleServiceZhDescriptionChange = (serviceId: string, zh_description: string): void => {
    setSuggestedServices(prev =>
      prev.map(service =>
        service.id === serviceId ? { ...service, zh_description } : service
      )
    );
  };

  const handleRemoveService = async (serviceId: string): Promise<void> => {
    try {
      // Delete the AI-generated plan from database
      await deletePlan(serviceId);
      console.log('Deleted AI-generated plan:', serviceId);

      // Remove from UI
      setSuggestedServices(prev => prev.filter(service => service.id !== serviceId));

      toast.success(t('instagramShowcase.services.removed', 'Service removed'));
    } catch (error: unknown) {
      console.error('Error deleting plan:', error);
      const err = error as { code?: string; message?: string };

      // Handle backend crash / connection reset
      if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
        console.warn('Backend may have crashed during deletion');
        // Still remove from UI - plan is likely deleted
        setSuggestedServices(prev => prev.filter(service => service.id !== serviceId));
        toast.warning('Service removed (server had an issue, please refresh to verify)');
      } else {
        toast.error(t('instagramShowcase.services.removeError', 'Failed to remove service'));
      }
    }
  };

  // Restore functionality removed - once deleted, plan is gone
  // Users can add it back as custom service if needed

  const handleAddCustomService = (): void => {
    if (!newServiceName || !newServiceZhName || !newServicePrice || !newServiceTypeId) {
      onError(t('instagramShowcase.services.fillAllFields', 'Please fill in all fields for the new service'));
      return;
    }

    const newService: SuggestedService = {
      id: `custom-${Date.now()}`,
      name: newServiceName,
      zh_name: newServiceZhName,
      description: '',
      zh_description: '',
      price: newServicePrice,
      serviceTypeId: newServiceTypeId,
      subServiceTypeId: newSubServiceTypeId || null,
      isEnabled: true,
      source: 'custom'
    };

    setCustomServices(prev => [...prev, newService]);
    setNewServiceName('');
    setNewServiceZhName('');
    setNewServicePrice('');
    setNewServiceTypeId('');
    setNewSubServiceTypeId('');
  };

  const handleRemoveCustomService = (serviceId: string): void => {
    setCustomServices(prev => prev.filter(service => service.id !== serviceId));
  };

  const handleCreateServices = async (): Promise<void> => {
    const enabledSuggested = suggestedServices.filter(service => service.isEnabled && service.price);
    const enabledCustom = customServices.filter(service => service.isEnabled && service.price);

    if (enabledSuggested.length === 0 && enabledCustom.length === 0) {
      onError(t('instagramShowcase.services.atLeastOne', 'Please keep at least one service'));
      return;
    }

    setIsLoading(true);
    onError('');

    try {
      // Fetch existing active plans to prevent duplicates
      const existingPlansResponse = await getPlans({
        staffId: flowData.staffId ?? undefined,
        isActive: true
      });
      const existingPlans = existingPlansResponse?.data || [];

      // Filter out AI-suggested services that are already active (to avoid duplicate updates)
      const suggestedToUpdate = enabledSuggested.filter(service => {
        // Check if this plan ID already exists and is active
        const alreadyActive = existingPlans.some((plan: Record<string, unknown>) =>
          plan._id === service.id && plan.isActive
        );
        return !alreadyActive;
      });

      // Filter out custom services that already exist
      const customToCreate = enabledCustom.filter(service => {
        const exists = existingPlans.some((plan: Record<string, unknown>) =>
          (plan.name as string).toLowerCase() === service.name.toLowerCase() &&
          plan.serviceTypeId?.toString() === service.serviceTypeId?.toString()
        );
        return !exists;
      });

      // Log if any duplicates were skipped
      const skippedSuggested = enabledSuggested.length - suggestedToUpdate.length;
      const skippedCustom = enabledCustom.length - customToCreate.length;
      if (skippedSuggested > 0 || skippedCustom > 0) {
        console.log(`Skipped ${skippedSuggested} AI-suggested and ${skippedCustom} custom services (already exist)`);
      }

      // Update AI-generated plans in parallel
      const updatePromises = suggestedToUpdate.map(service =>
        updatePlan({
          _id: service.id,
          name: service.name,
          zh_name: service.zh_name,
          description: service.description,
          zh_description: service.zh_description,
          price: parseFloat(String(service.price)),
          isActive: true // Activate the plan
        })
      );

      // Create new plans from custom services in parallel
      const createPromises = customToCreate.map(service =>
        createPlan({
          name: service.name,
          zh_name: service.zh_name,
          description: service.description || `${service.name} service`,
          zh_description: service.zh_description || `${service.zh_name}服務`,
          price: parseFloat(String(service.price)),
          duration: 60,
          serviceTypeId: service.serviceTypeId,
          subServiceTypeId: service.subServiceTypeId,
          businessId: flowData.businessId,
          staffIds: [flowData.staffId],
          isActive: true,
          source: 'manual'
        })
      );

      // Execute all updates and creates in parallel
      const [updatedPlans, createdPlans] = await Promise.all([
        Promise.all(updatePromises),
        Promise.all(createPromises)
      ]);

      const finalServices = [...updatedPlans, ...createdPlans];

      // Check if any suggested services were already active (editing existing)
      const hasActiveServices = enabledSuggested.some(s => s.isActive);

      console.log(`${hasActiveServices ? 'Updated' : 'Activated'} ${updatedPlans.length} AI-generated plans`);
      console.log(`Created ${createdPlans.length} custom plans`);

      // Update flow data
      onUpdate({
        createdServices: finalServices,
        servicesComplete: true
      });

      const successMessage = hasActiveServices
        ? t('instagramShowcase.services.updated', '{{count}} service(s) updated!', { count: finalServices.length })
        : t('instagramShowcase.services.success', '{{count}} service(s) activated!', { count: finalServices.length });

      toast.success(successMessage);

      // Complete the flow
      onComplete();

    } catch (error: unknown) {
      console.error('Error activating/creating services:', error);
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = err.response?.data?.message || err.message || 'Failed to activate services';
      onError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const enabledServicesCount = suggestedServices.filter(s => s.isEnabled && s.price).length +
                             customServices.filter(s => s.price).length;

  return (
    <Box className="service-creation-step">
      <Typography variant="h5" gutterBottom>
        {t('instagramShowcase.services.title', 'Create Your Services')}
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        {t('instagramShowcase.services.description', 'Set up your services with prices. AI has suggested services based on your Instagram profile.')}
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Progress Alert */}
      <Alert
        severity={enabledServicesCount > 0 ? 'success' : 'warning'}
        sx={{ mb: 3 }}
        icon={enabledServicesCount > 0 ? <CheckCircle /> : <AttachMoney />}
      >
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {enabledServicesCount > 0
            ? t('instagramShowcase.services.ready', 'Great! You have {{count}} service(s) ready to publish.', { count: enabledServicesCount })
            : t('instagramShowcase.services.needServices', 'Please add at least one service with a price to continue.')
          }
        </Typography>
      </Alert>

      {/* AI Suggested Services */}
      {suggestedServices.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AutoFixHigh color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              {suggestedServices.some(s => s.isActive)
                ? t('instagramShowcase.services.yourServices', 'Your Services')
                : t('instagramShowcase.services.aiSuggested', 'AI Suggested Services')
              }
            </Typography>
          </Box>
          {suggestedServices.some(s => s.isActive) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('instagramShowcase.services.editingActive', 'You are editing your existing active services. Any changes will be saved immediately.')}
            </Alert>
          )}

          <Grid container spacing={2}>
            {suggestedServices.map((service) => (
              <Grid size={{ xs: 12, md: 6 }} key={service.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {service.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {service.zh_name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Chip
                          size="small"
                          label="AI"
                          color="primary"
                          icon={<AutoFixHigh />}
                        />
                        {service.isActive && (
                          <Chip
                            size="small"
                            label={t('instagramShowcase.services.active', 'Active')}
                            color="success"
                            icon={<CheckCircle />}
                          />
                        )}
                      </Box>
                    </Box>

                    <Box>
                      <Box>
                        <Box sx={{ mt: 1, mb: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            onClick={() => handleServiceDescriptionTranslate(
                              service.id,
                              service.description,
                              'en',
                              'zh-HK',
                              'zh_description'
                            )}
                            disabled={
                              translatingServices[service.id]?.enToZh ||
                              translatingServices[service.id]?.zhToEn ||
                              !service.description
                            }
                            variant="outlined"
                            size="small"
                            startIcon={
                              translatingServices[service.id]?.enToZh
                                ? <CircularProgress size={16} />
                                : <AutoFixHigh />
                            }
                          >
                            {t('quickListingWizard.translateWithAI', 'Translate with AI')} {t('common.toChinese', 'to Chinese')}
                          </Button>
                        </Box>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label={t('instagramShowcase.services.description', 'Description')}
                          value={service.description}
                          onChange={(e) => handleServiceDescriptionChange(service.id, e.target.value)}
                          placeholder={t('instagramShowcase.services.descriptionPlaceholder', 'Describe what makes this service special...')}
                          disabled={translatingServices[service.id]?.enToZh || translatingServices[service.id]?.zhToEn}
                          sx={{ mb: 2 }}
                        />
                      </Box>
                      <Box>
                        <Box sx={{ mt: 1, mb: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            onClick={() => handleServiceDescriptionTranslate(
                              service.id,
                              service.zh_description,
                              'zh-HK',
                              'en',
                              'description'
                            )}
                            disabled={
                              translatingServices[service.id]?.enToZh ||
                              translatingServices[service.id]?.zhToEn ||
                              !service.zh_description
                            }
                            variant="outlined"
                            size="small"
                            startIcon={
                              translatingServices[service.id]?.zhToEn
                                ? <CircularProgress size={16} />
                                : <AutoFixHigh />
                            }
                          >
                            {t('quickListingWizard.translateWithAI', 'Translate with AI')} {t('common.toEnglish', 'to English')}
                          </Button>
                        </Box>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          label={t('instagramShowcase.services.zhDescription', 'Chinese Description')}
                          value={service.zh_description}
                          onChange={(e) => handleServiceZhDescriptionChange(service.id, e.target.value)}
                          placeholder={t('instagramShowcase.services.zhDescriptionPlaceholder', '描述此服務的特色...')}
                          disabled={translatingServices[service.id]?.enToZh || translatingServices[service.id]?.zhToEn}
                          sx={{ mb: 2 }}
                        />
                      </Box>
                      <TextField
                        fullWidth
                        required
                        label={t('instagramShowcase.services.price', 'Price (HKD)')}
                        value={service.price}
                        onChange={(e) => handleServicePriceChange(service.id, e.target.value)}
                        onWheel={(e) => (e.target as HTMLInputElement).blur()} // Prevent scroll from changing value
                        type="number"
                        inputProps={{ min: 0, step: 10 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">HK$</InputAdornment>
                        }}
                        error={!service.price || Number(service.price) <= 0}
                        helperText={!service.price || Number(service.price) <= 0
                          ? t('instagramShowcase.services.priceRequired', 'Price is required')
                          : t('instagramShowcase.services.priceHelper', 'Please enter the price for this service')}
                        sx={{
                          mb: 2,
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: !service.price || Number(service.price) <= 0 ? 'rgba(255, 152, 0, 0.04)' : 'transparent'
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<Delete />}
                        onClick={() => handleRemoveService(service.id)}
                      >
                        {t('instagramShowcase.services.remove', 'Remove')}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Custom Services */}
      {customServices.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            {t('instagramShowcase.services.customServices', 'Custom Services')}
          </Typography>

          <Grid container spacing={2}>
            {customServices.map((service) => (
              <Grid size={{ xs: 12, md: 6 }} key={service.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {service.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {service.zh_name}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveCustomService(service.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                      HK${service.price}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Add Custom Service */}
      <Card variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('instagramShowcase.services.addCustom', 'Add Custom Service')}
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth required>
                <InputLabel>{t('instagramShowcase.services.serviceType', 'Service Type')}</InputLabel>
                <Select
                  value={newServiceTypeId}
                  onChange={(e) => setNewServiceTypeId(e.target.value)}
                  label={t('instagramShowcase.services.serviceType', 'Service Type')}
                >
                  {serviceTypes.map((type) => (
                    <MenuItem key={type._id} value={type._id}>
                      {type.name} ({type.zh_name})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Required</FormHelperText>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth disabled={!newServiceTypeId}>
                <InputLabel>{t('instagramShowcase.services.subServiceType', 'Sub Type')}</InputLabel>
                <Select
                  value={newSubServiceTypeId}
                  onChange={(e) => setNewSubServiceTypeId(e.target.value)}
                  label={t('instagramShowcase.services.subServiceType', 'Sub Type')}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {subServiceTypes.map((subType) => (
                    <MenuItem key={subType._id} value={subType._id}>
                      {subType.name} ({subType.zh_name})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Optional</FormHelperText>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                required
                label={t('instagramShowcase.services.serviceName', 'Service Name (English)')}
                value={newServiceName}
                onChange={(e) => setNewServiceName(e.target.value)}
                placeholder="e.g. Hair Cut"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                required
                label={t('instagramShowcase.services.serviceNameZh', 'Service Name (Chinese)')}
                value={newServiceZhName}
                onChange={(e) => setNewServiceZhName(e.target.value)}
                placeholder="例如：剪髮"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label={t('instagramShowcase.services.price', 'Price (HKD)')}
                value={newServicePrice}
                onChange={(e) => setNewServicePrice(e.target.value)}
                onWheel={(e) => (e.target as HTMLInputElement).blur()} // Prevent scroll from changing value
                type="number"
                inputProps={{ min: 0, step: 10 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">HK$</InputAdornment>
                }}
              />
            </Grid>
          </Grid>

          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddCustomService}
            sx={{ mt: 2 }}
            disabled={!newServiceName || !newServiceZhName || !newServicePrice || !newServiceTypeId}
          >
            {t('instagramShowcase.services.addService', 'Add Service')}
          </Button>
        </CardContent>
      </Card>

      {/* Summary */}
      {enabledServicesCount > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {t('instagramShowcase.services.summary', 'You are about to create {{count}} service(s). These will be available for client booking immediately.', {
              count: enabledServicesCount
            })}
          </Typography>
        </Alert>
      )}

      {/* Navigation buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={isLoading}
        >
          {t('instagramShowcase.services.back', 'Back')}
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={handleCreateServices}
          disabled={enabledServicesCount === 0 || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : <CheckCircle />}
          sx={{ px: 4 }}
        >
          {isLoading
            ? t('instagramShowcase.services.creating', 'Creating Services...')
            : t('instagramShowcase.services.complete', 'Complete Setup')
          }
        </Button>
      </Box>
    </Box>
  );
};

export default ServiceCreationStep;
